import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config/api';

interface ApiKey {
  id: string;    
  keyName: string;
  keyPrefix: string;
  permissions: Record<string, any>;
  isActive: boolean;
  lastUsedAt: string | null;
  usageCount: number;
  rateLimit: number | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CreateApiKeyData {
  keyName: string;
  permissions: Record<string, boolean>;
  rateLimit: number | null;
  expiresAt: string | null;
}

interface Subscription {
  planType: 'FREE' | 'BUSINESS' | 'ENTERPRISE';
}

const ApiKeys: React.FC = () => {
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createLoading, setCreateLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateApiKeyData>({
    keyName: '',
    permissions: {
      read: true,
      write: false,
      admin: false
    },
    rateLimit: null,
    expiresAt: null
  });

  // API call helper
  const apiCall = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return null;
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });

    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
      return null;
    }

    return response;
  };

  // Fetch subscription status
  const fetchSubscription = async () => {
    try {
      const response = await apiCall('/api/subscription');
      if (!response) return;

      const data = await response.json();
      if (data.success) {
        setSubscription(data.subscription);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    }
  };

  // Fetch API keys
  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/keys');
      if (!response) return;

      const data = await response.json();
      if (data.success) {
        setApiKeys(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch API keys');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Create API key
  const createApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check subscription plan
    if (subscription?.planType === 'FREE') {
      setError('API key creation is only available for Business or Enterprise plans. Please upgrade your subscription.');
      return;
    }

    if (!formData.keyName.trim()) {
      setError('Key name is required');
      return;
    }

    try {
      setCreateLoading(true);
      setError(null);

      const requestData = {
        keyName: formData.keyName.trim(),
        permissions: formData.permissions,
        rateLimit: formData.rateLimit || null,
        expiresAt: formData.expiresAt || null
      };

      const response = await apiCall('/api/keys', {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      if (!response) return;

      const data = await response.json();
      if (data.success) {
        setCreatedKey(data.data.apiKey);
        setFormData({
          keyName: '',
          permissions: { read: true, write: false, admin: false },
          rateLimit: null,
          expiresAt: null
        });
        fetchApiKeys(); // Refresh the list
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to create API key');
      console.error(err);
    } finally {
      setCreateLoading(false);
    }
  };

  // Delete API key
  const deleteApiKey = async (keyId: string, keyName: string) => {
    try {
      const response = await apiCall(`/api/keys/${keyId}`, {
        method: 'DELETE'
      });

      if (!response) return;

      const data = await response.json();
      if (data.success) {
        fetchApiKeys(); // Refresh the list
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to delete API key');
      console.error(err);
    }
  };

  // Toggle API key status
  const toggleApiKey = async (keyId: string, currentStatus: boolean) => {
    try {
      const response = await apiCall(`/api/keys/${keyId}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (!response) return;

      const data = await response.json();
      if (data.success) {
        fetchApiKeys(); // Refresh the list
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to update API key');
      console.error(err);
    }
  };

  useEffect(() => {
    Promise.all([fetchSubscription(), fetchApiKeys()])
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-gray-700 mr-4"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              disabled={subscription?.planType === 'FREE'}
              className={`px-4 py-2 rounded-lg ${
                subscription?.planType === 'FREE'
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Create API Key
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Error Display */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-800">{error}</div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 text-sm underline mt-2"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* API Keys List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : apiKeys.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500 mb-4">No API keys found</div>
                {subscription?.planType === 'FREE' ? (
                  <div className="text-gray-600">
                    API key creation is only available for Business or Enterprise plans.{' '}
                    <button
                      onClick={() => navigate('/subscription')}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      Upgrade your plan
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    Create your first API key
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {apiKeys.map((key) => (
                  <div key={key.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h3 className="text-lg font-medium text-gray-900">{key.keyName}</h3>
                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                            key.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {key.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Key:</span> {key.keyPrefix}
                          </div>
                          <div>
                            <span className="font-medium">Usage:</span> {key.usageCount} calls
                          </div>
                          <div>
                            <span className="font-medium">Last Used:</span> {formatDate(key.lastUsedAt)}
                          </div>
                          <div>
                            <span className="font-medium">Created:</span> {formatDate(key.createdAt)}
                          </div>
                        </div>
                        {key.rateLimit && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Rate Limit:</span> {key.rateLimit} calls
                          </div>
                        )}
                        {key.expiresAt && (
                          <div className="mt-1 text-sm text-gray-600">
                            <span className="font-medium">Expires:</span> {formatDate(key.expiresAt)}
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => toggleApiKey(key.id, key.isActive)}
                          className={`px-3 py-1 text-sm rounded ${
                            key.isActive
                              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                              : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                        >
                          {key.isActive ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => deleteApiKey(key.id, key.keyName)}
                          className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Create API Key</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreatedKey(null);
                    setError(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {subscription?.planType === 'FREE' ? (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="text-yellow-800 font-medium mb-2">Upgrade Required</h3>
                    <p className="text-yellow-600 text-sm mb-3">
                      API key creation is only available for Business or Enterprise plans.
                    </p>
                    <button
                      onClick={() => navigate('/subscription')}
                      className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                    >
                      View Subscription Plans
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setCreatedKey(null);
                    }}
                    className="w-full border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              ) : createdKey ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="text-green-800 font-medium mb-2">API Key Created!</h3>
                    <p className="text-green-600 text-sm mb-3">
                      Please copy your API key now. You won't be able to see it again!
                    </p>
                    <div className="bg-white border rounded p-3 break-all font-mono text-sm">
                      {createdKey}
                    </div>
                    <button
                      onClick={() => copyToClipboard(createdKey)}
                      className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
                    >
                      Copy to clipboard
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setCreatedKey(null);
                    }}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={createApiKey} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Key Name *
                    </label>
                    <input
                      type="text"
                      value={formData.keyName}
                      onChange={(e) => setFormData({...formData, keyName: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="My API Key"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Permissions
                    </label>
                    <div className="space-y-2">
                      {Object.entries(formData.permissions).map(([perm, value]) => (
                        <label key={perm} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={(e) => setFormData({
                              ...formData,
                              permissions: {
                                ...formData.permissions,
                                [perm]: e.target.checked
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 capitalize">{perm}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rate Limit (optional)
                    </label>
                    <input
                      type="number"
                      value={formData.rateLimit || ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        rateLimit: e.target.value ? parseInt(e.target.value) : null
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="1000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expires At (optional)
                    </label>
                    <input
                      type="date"
                      value={formData.expiresAt || ''}
                      onChange={(e) => setFormData({...formData, expiresAt: e.target.value || null})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateModal(false);
                        setError(null);
                      }}
                      className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createLoading}
                      className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                    >
                      {createLoading ? 'Creating...' : 'Create Key'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeys;